import React, { createContext, useContext, useEffect, useState } from 'react'
import { availableLanguages, normalizeLanguage, toHtmlLanguage, type Language } from '../i18n/translations'

interface LanguageContextType {
    lang: Language
    setLang: (lang: Language) => void
    secondaryLang: Language | null
    setSecondaryLang: (lang: Language | null) => void
    availableLanguages: typeof availableLanguages
}

const LANGUAGE_STORAGE_KEY = 'language'
const LEGACY_LANGUAGE_STORAGE_KEY = 'quiz_language'
const SECONDARY_LANGUAGE_STORAGE_KEY = 'secondary_language'
const LEGACY_BILINGUAL_DUTCH_STORAGE_KEY = 'quiz_bilingual_dutch'

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

function readInitialLanguage(): Language {
    if (typeof window === 'undefined') return 'nl'

    const queryLanguage = normalizeLanguage(new URLSearchParams(window.location.search).get('lang'))
    if (queryLanguage) {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, queryLanguage)
        localStorage.removeItem(LEGACY_LANGUAGE_STORAGE_KEY)
        return queryLanguage
    }

    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY)
    const savedLanguage = normalizeLanguage(saved)
    if (savedLanguage) {
        if (saved !== savedLanguage) localStorage.setItem(LANGUAGE_STORAGE_KEY, savedLanguage)
        return savedLanguage
    }

    const legacy = localStorage.getItem(LEGACY_LANGUAGE_STORAGE_KEY)
    const legacyLanguage = normalizeLanguage(legacy)
    if (legacyLanguage) {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, legacyLanguage)
        localStorage.removeItem(LEGACY_LANGUAGE_STORAGE_KEY)
        return legacyLanguage
    }

    if (saved !== null) localStorage.removeItem(LANGUAGE_STORAGE_KEY)
    if (legacy !== null) localStorage.removeItem(LEGACY_LANGUAGE_STORAGE_KEY)
    return 'nl'
}

function readInitialSecondaryLanguage(primary: Language): Language | null {
    if (typeof window === 'undefined') return null
    const saved = localStorage.getItem(SECONDARY_LANGUAGE_STORAGE_KEY)
    const savedLanguage = normalizeLanguage(saved)
    if (savedLanguage && savedLanguage !== primary) {
        if (saved !== savedLanguage) localStorage.setItem(SECONDARY_LANGUAGE_STORAGE_KEY, savedLanguage)
        return savedLanguage
    }

    // Migrate the short-lived Dutch-only bilingual preference.
    if (localStorage.getItem(LEGACY_BILINGUAL_DUTCH_STORAGE_KEY) === 'true' && primary !== 'nl') {
        localStorage.setItem(SECONDARY_LANGUAGE_STORAGE_KEY, 'nl')
        localStorage.removeItem(LEGACY_BILINGUAL_DUTCH_STORAGE_KEY)
        return 'nl'
    }

    if (saved !== null) localStorage.removeItem(SECONDARY_LANGUAGE_STORAGE_KEY)
    localStorage.removeItem(LEGACY_BILINGUAL_DUTCH_STORAGE_KEY)
    return null
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [lang, setLangState] = useState<Language>(readInitialLanguage)
    const [secondaryLang, setSecondaryLangState] = useState<Language | null>(() => readInitialSecondaryLanguage(readInitialLanguage()))

    useEffect(() => {
        document.documentElement.lang = toHtmlLanguage(lang)
        document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr'
    }, [lang])

    const setLang = (newLang: Language) => {
        setLangState(newLang)
        if (typeof window !== 'undefined') {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang)
            localStorage.removeItem(LEGACY_LANGUAGE_STORAGE_KEY)
        }
        if (secondaryLang === newLang) {
            setSecondaryLangState(null)
            if (typeof window !== 'undefined') localStorage.removeItem(SECONDARY_LANGUAGE_STORAGE_KEY)
        }
    }

    const setSecondaryLang = (newLang: Language | null) => {
        const next = newLang === lang ? null : newLang
        setSecondaryLangState(next)
        if (typeof window !== 'undefined') {
            if (next) localStorage.setItem(SECONDARY_LANGUAGE_STORAGE_KEY, next)
            else localStorage.removeItem(SECONDARY_LANGUAGE_STORAGE_KEY)
            localStorage.removeItem(LEGACY_BILINGUAL_DUTCH_STORAGE_KEY)
        }
    }

    return (
        <LanguageContext.Provider value={{ lang, setLang, secondaryLang, setSecondaryLang, availableLanguages }}>
            {children}
        </LanguageContext.Provider>
    )
}

export const useLanguage = () => {
    const context = useContext(LanguageContext)
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider')
    }
    return context
}

export type { Language }
